import type { Gait, Phase } from './Gait';

const SOUNDS = ['hoof', 'wobble', 'fall', 'recover'] as const;
type Sound = typeof SOUNDS[number];
const MUTE_KEY = 'fourbeat-muted';

/** One-shot effects follow simulation events; no looping canned gallop. */
export class GameAudio {
  muted = false;
  private context?: AudioContext;
  private master?: GainNode;
  private loading?: Promise<void>;
  private buffers = new Map<Sound, AudioBuffer>();
  private sources = new Set<AudioBufferSourceNode>();
  private errors: string[] = [];
  private played: Record<Sound, number> = { hoof: 0, wobble: 0, fall: 0, recover: 0 };
  private steps = [0, 0, 0, 0];
  private phase: Phase = 'ready';
  private danger = false;
  private lastWarning = -Infinity;
  constructor() {
    try { this.muted = localStorage.getItem(MUTE_KEY) === 'true'; } catch { /* Storage is optional. */ }
  }
  /** Call synchronously from a real gesture so Safari can unlock playback. */
  unlock() {
    if (this.muted) return;
    try {
      if (!this.context) {
        this.context = new AudioContext();
        this.master = this.context.createGain();
        this.master.gain.value = .65;
        this.master.connect(this.context.destination);
      }
      if (this.context.state !== 'running') void this.context.resume().catch(() => {
        // A later genuine gesture can retry a browser-denied resume.
      });
      this.loading ??= this.load(this.context);
    } catch (error) { this.report('Audio unavailable', error); }
  }
  private async load(context: AudioContext) {
    await Promise.all(SOUNDS.map(async sound => {
      try {
        const response = await fetch(`${import.meta.env.BASE_URL}assets/audio/${sound}.mp3`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this.buffers.set(sound, await context.decodeAudioData(await response.arrayBuffer()));
      } catch (error) { this.report(`Could not load ${sound}`, error); }
    }));
  }
  private report(message: string, error: unknown) {
    if (!this.errors.includes(message)) { this.errors.push(message); console.warn(message, error); }
  }
  toggleMute() {
    this.muted = !this.muted;
    if (this.master && this.context) this.master.gain.setValueAtTime(this.muted ? 0 : .65, this.context.currentTime);
    if (this.muted) this.stop(); else this.unlock();
    try { localStorage.setItem(MUTE_KEY, String(this.muted)); } catch { /* Private browsing may block writes. */ }
  }
  stop() {
    for (const source of this.sources) source.stop();
    this.sources.clear();
  }
  reset() {
    this.stop(); this.steps.fill(0); this.phase = 'ready';
    this.danger = false; this.lastWarning = -Infinity;
  }
  update(game: Gait, paused: boolean) {
    const previous = this.phase;
    this.phase = game.phase;
    if (paused) { this.stop(); return; }
    if (game.phase === 'fallen' && previous !== 'fallen') {
      this.stop(); this.play('fall', .8);
    } else if (game.phase === 'running') {
      if (previous === 'fallen') this.play('recover', .55);
      for (let i = 0; i < 4; i++) if (game.legs[i].steps > this.steps[i]) {
        // Small repeatable timbre differences keep individual feet readable.
        const rate = [1.06, .97, .92, 1.01][i] + (game.legs[i].steps % 3 - 1) * .025;
        this.play('hoof', .28 + Math.min(game.speed, 4) * .025, rate, i % 2 ? .18 : -.18);
      }
      if (!this.danger && game.balanceRisk > .62 && game.time - this.lastWarning > .8) {
        this.play('wobble', .35); this.lastWarning = game.time;
      }
    } else if (game.phase === 'won' || game.phase === 'lost') this.stop();
    if (game.balanceRisk > .62) this.danger = true;
    else if (game.balanceRisk < .35) this.danger = false;
    for (let i = 0; i < 4; i++) this.steps[i] = game.legs[i].steps;
  }
  private play(sound: Sound, volume: number, rate = 1, pan = 0) {
    const context = this.context, buffer = this.buffers.get(sound);
    if (this.muted || !context || !this.master || context.state !== 'running' || !buffer || this.sources.size >= 8) return;
    const source = context.createBufferSource(), gain = context.createGain(), stereo = context.createStereoPanner();
    source.buffer = buffer; source.playbackRate.value = rate;
    gain.gain.value = volume; stereo.pan.value = pan;
    source.connect(gain); gain.connect(stereo); stereo.connect(this.master);
    source.onended = () => { this.sources.delete(source); source.disconnect(); gain.disconnect(); stereo.disconnect(); };
    this.sources.add(source); this.played[sound]++; source.start();
  }
  snapshot() {
    return { muted: this.muted, state: this.context?.state ?? 'locked', loaded: [...this.buffers.keys()],
      played: { ...this.played }, voices: this.sources.size, errors: [...this.errors] };
  }
}
